import { useEffect, useState } from "react";
import { routeFrom } from "../viewer/GridViewer";

/**
 * Canonical routing types + helpers (WO#2 §21).
 *
 * The hash-router dispatch semantics MUST remain semantically identical
 * to the pre-WO#1 App.tsx baseline (see git show
 * `d32d53540c7f8c716b814b53d533bef0f33e9594:frontend/src/App.tsx`):
 *
 *   - "" (empty / default) → GridViewer (public read-only viewer, Phase 1C)
 *   - "grid"               → GridViewer
 *   - "purchase"           → PurchaseRoute (Grid in select-mode + PurchasePanel)
 *   - "auth"               → AuthPanel (SIWE — Phase 0)
 *   - "dashboard"          → DashboardRoute (WO#3 — owner My Territories)
 *   - any other hash       → GridViewer (fallback via routeFrom)
 *
 * A vitest suite pins this dispatch (tests/routing.test.ts).
 */
export type PrimaryView = "grid" | "purchase" | "auth" | "dashboard" | "canvas" | "voxel_editor" | "publication_public" | "profile" | "artwork_public" | "admin" | "about" | "how_it_works" | "explore" | "creators" | "onboarding" | "marketplace" | "feed" | "creator_profile" | "legal" | "rent";

/** Public creator profile route: `#u/<wallet|handle|me>`. Returns the id or null. */
export function creatorFromRoute(route: string): string | null {
  if (!route.startsWith("u/")) return null;
  const id = route.slice("u/".length).split(/[?#]/)[0].trim();
  return id.length > 0 ? id : null;
}

/** Canvas editor route: `#canvas/<identity_id>`. Returns the id or null. */
export function canvasIdentityFromRoute(route: string): string | null {
  if (!route.startsWith("canvas/")) return null;
  const id = route.slice("canvas/".length).split(/[?#]/)[0].trim();
  return id.length > 0 ? id : null;
}

/** XYZ voxel editor route (Macro #26): `#voxel/<identity_id>`. Returns id/null. */
export function voxelIdentityFromRoute(route: string): string | null {
  if (!route.startsWith("voxel/")) return null;
  const id = route.slice("voxel/".length).split(/[?#]/)[0].trim();
  return id.length > 0 ? id : null;
}

/** Public publication route: `#p/<identity_id>`. Returns the id or null. */
export function publicPublicationFromRoute(route: string): string | null {
  if (!route.startsWith("p/")) return null;
  const id = route.slice("p/".length).split(/[?#]/)[0].trim();
  return id.length > 0 ? id : null;
}

/** Frozen Artwork detail route: `#artwork/<id>`. Returns the id or null. */
export function artworkIdFromRoute(route: string): string | null {
  if (!route.startsWith("artwork/")) return null;
  const id = route.slice("artwork/".length).split(/[?#]/)[0].trim();
  return id.length > 0 ? id : null;
}

export function viewFromRoute(route: string): PrimaryView {
  if (route === "auth") return "auth";
  if (route === "admin") return "admin";
  if (route === "dashboard") return "dashboard";
  if (route === "onboarding") return "onboarding";
  if (route === "marketplace" || route.startsWith("marketplace/") || route.startsWith("marketplace?")) return "marketplace";
  if (route === "feed" || route.startsWith("feed/") || route.startsWith("feed?")) return "feed";
  if (creatorFromRoute(route)) return "creator_profile";
  // Macro #26 · Increment 3 — dedicated PUBLIC information pages. Literal routes
  // (never sections spliced into the frozen Home). `#roadmap` is NOT exposed as a
  // nav voice anymore; it keeps its legacy meaning (falls through to the Home
  // grid view) so the frozen Home body's Roadmap anchor stays reachable.
  if (route === "about") return "about";
  if (route === "how-it-works") return "how_it_works";
  if (route === "explore") return "explore";
  if (route === "creators") return "creators";
  // Parcel RENT — PUBLIC informational preview of a planned feature (#rent).
  if (route === "rent") return "rent";
  // Macro #33 · W7 — DRAFT legal / privacy surfaces (#legal, #legal/<doc>).
  if (route === "legal" || route.startsWith("legal/")) return "legal";
  if (route === "profile" || route.startsWith("profile/")) return "profile";
  if (canvasIdentityFromRoute(route)) return "canvas";
  if (voxelIdentityFromRoute(route)) return "voxel_editor";
  if (publicPublicationFromRoute(route)) return "publication_public";
  if (artworkIdFromRoute(route)) return "artwork_public";
  if (route === "purchase") return "purchase";
  if (routeFrom(route) === "grid") return "grid";
  return "grid"; // canonical fallback — default = public GridViewer
}

export function useHashRoute(): string {
  const [route, setRoute] = useState<string>(() =>
    typeof window !== "undefined"
      ? window.location.hash.replace(/^#/, "")
      : "",
  );
  useEffect(() => {
    const onH = () => setRoute(window.location.hash.replace(/^#/, ""));
    window.addEventListener("hashchange", onH);
    return () => window.removeEventListener("hashchange", onH);
  }, []);
  return route;
}
